<template>
  <v-dialog v-model="dialog" max-width="720" persistent>
    <v-card class="delivery-card">
      <!-- HEADER -->
      <div class="header">
        <div>
          <h2 class="title">Edit Delivery Group</h2>
          <p class="subtitle">
            Update the details of the selected delivery group
          </p>
        </div>

        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- BODY -->
      <v-card-text class="body">

        <!-- Group Name -->
        <div class="field">
          <label class="label">
            Delivery Group Name<span class="required">*</span>
          </label>
          <v-text-field v-model="form.name" placeholder="e.g. Market Run Crew" outlined dense hide-details />
        </div>

        <!-- Area -->
        <div class="field">
          <label class="label">
            Select Area<span class="required">*</span>
          </label>
          <v-select :items="areas" item-text="name" item-value="id" v-model="selectedArea"
            placeholder="Select Area" outlined dense hide-details />
        </div>

        <!-- Businesses -->
        <div class="field">
          <label class="label">
            Select Businesses<span class="required">*</span>
          </label>

          <v-select multiple :items="filteredRestaurants" item-text="name" item-value="id"
            v-model="form.restaurant_ids" placeholder="Select Businesses for this group" outlined dense hide-details />

          <div class="chips">
            <v-chip v-for="id in form.restaurant_ids" :key="id" close @click:close="removeRestaurant(id)"
              class="chip">
              {{ getRestaurantName(id) }}
            </v-chip>
          </div>
        </div>

        <!-- Status Toggle -->
        <div class="field">
          <v-switch v-model="form.status" label="Active" inset></v-switch>
        </div>

        <!-- Waiting Location -->
        <div class="field">
          <label class="label">
            Preferred Waiting Location<span class="required">*</span>
          </label>
          <v-text-field :value="waitingLocationLabel" placeholder="Click on map to select" outlined dense
            hide-details readonly />
        </div>

        <!-- MAP -->
        <div class="map-container">
          <div ref="map" class="real-map"></div>
        </div>

      </v-card-text>

      <!-- FOOTER -->
      <div class="footer">
        <v-btn large outlined class="cancel-btn" @click="closeDialog">
          Cancel
        </v-btn>

        <v-btn large class="save-btn" :loading="$store.state.loading" @click="updateGroup">
          Save
        </v-btn>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import restaurantIconSvg from "@/assets/restaurant-marker.svg"
import waitingIconSvg from "@/assets/waiting-location-marker.svg"

export default {
  name: "EditRestaurantGroupModal",

  props: {
    group: { type: Object, required: true } // pass the group to edit
  },

  data() {
    return {
      dialog: true,
      map: null,
      restaurantMarkers: [],
      waitingMarker: null,
      selectedArea: null,
      form: {
        name: "",
        area_id: null,
        restaurant_ids: [],
        waiting_lat: null,
        waiting_lng: null,
        status: true // default active
      }
    }
  },

  computed: {
    areas() {
      return this.$store.getters.areas || []
    },

    restaurants() {
      return this.$store.getters.restaurants || []
    },

    filteredRestaurants() {
      if (!this.selectedArea) return []
      return this.restaurants.filter(r => r.area_id === this.selectedArea)
    },

    waitingLocationLabel() {
      if (!this.form.waiting_lat || !this.form.waiting_lng) return ""
      const lat = Number(this.form.waiting_lat)
      const lng = Number(this.form.waiting_lng)
      if (isNaN(lat) || isNaN(lng)) return ""
      return `${lat.toFixed(5)}, ${lng.toFixed(5)}`
    }
  },

  watch: {
    selectedArea(newVal) {
      this.form.area_id = newVal
      this.form.restaurant_ids = this.form.restaurant_ids.filter(id =>
        this.filteredRestaurants.some(r => r.id === id)
      )
      this.renderRestaurantMarkers()
      this.centerToArea()
    },

    "form.restaurant_ids"() {
      this.renderRestaurantMarkers()
    }
  },

  async mounted() {
    this.restaurantIcon = L.icon({
      iconUrl: restaurantIconSvg,
      iconSize: [62, 62],
      iconAnchor: [21, 42],
      popupAnchor: [0, -38]
    })

    this.waitingIcon = L.icon({
      iconUrl: waitingIconSvg,
      iconSize: [62, 62],
      iconAnchor: [25, 50],
      popupAnchor: [0, -45]
    })

    if (!this.restaurants.length) await this.$store.dispatch("fetchRestaurants")
    if (!this.areas.length) await this.$store.dispatch("fetchAreas")

    this.prefillForm()
    this.initMap()
  },

  methods: {
    prefillForm() {
      this.form.name = this.group.name
      this.form.area_id = this.group.area_id
      this.selectedArea = this.group.area_id
      this.form.restaurant_ids = [...this.group.restaurants.map(r => r.id)]
      this.form.waiting_lat = this.group.waiting_lat !== null ? Number(this.group.waiting_lat) : null
      this.form.waiting_lng = this.group.waiting_lng !== null ? Number(this.group.waiting_lng) : null
      this.form.status = this.group.status !== undefined ? Boolean(this.group.status) : true
    },

    initMap() {
      const defaultLat = this.form.waiting_lat || 51.505
      const defaultLng = this.form.waiting_lng || -0.09
      this.map = L.map(this.$refs.map).setView([defaultLat, defaultLng], 13)

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(this.map)

      this.map.on("click", e => this.setWaitingLocation(e.latlng.lat, e.latlng.lng))

      this.renderRestaurantMarkers()

      if (this.form.waiting_lat && this.form.waiting_lng) {
        this.waitingMarker = L.marker(
          [this.form.waiting_lat, this.form.waiting_lng],
          { icon: this.waitingIcon }
        ).addTo(this.map)
      }
    },

    centerToArea() {
      const list = this.filteredRestaurants.filter(r => this.form.restaurant_ids.includes(r.id))
      if (!list.length) return
      const first = list.find(r => r.map_latitude && r.map_longitude)
      if (!first) return
      this.map.setView([Number(first.map_latitude), Number(first.map_longitude)], 14)
    },

    renderRestaurantMarkers() {
      if (!this.map) return
      this.restaurantMarkers.forEach(marker => this.map.removeLayer(marker))
      this.restaurantMarkers = []

      const selectedRestaurants = this.restaurants.filter(r => this.form.restaurant_ids.includes(r.id))
      selectedRestaurants.forEach(r => {
        if (!r.map_latitude || !r.map_longitude) return
        const marker = L.marker([r.map_latitude, r.map_longitude], { icon: this.restaurantIcon })
          .addTo(this.map)
          .bindPopup(r.name)
        this.restaurantMarkers.push(marker)
      })

      if (this.restaurantMarkers.length) {
        const group = new L.featureGroup(this.restaurantMarkers)
        this.map.fitBounds(group.getBounds().pad(0.2))
      }
    },

    setWaitingLocation(lat, lng) {
      this.form.waiting_lat = lat
      this.form.waiting_lng = lng

      if (this.waitingMarker) this.map.removeLayer(this.waitingMarker)
      this.waitingMarker = L.marker([lat, lng], { icon: this.waitingIcon }).addTo(this.map)
    },

    getRestaurantName(id) {
      const restaurant = this.restaurants.find(r => r.id === id)
      return restaurant ? restaurant.name : ''
    },

    removeRestaurant(id) {
      this.form.restaurant_ids = this.form.restaurant_ids.filter(r => r !== id)
    },

    closeDialog() {
      this.dialog = false
      this.$emit('close')
    },

    async updateGroup() {
      if (!this.form.name.trim()) {
        this.$store.dispatch('setError', 'Group name is required')
        return
      }
      if (!this.form.area_id) {
        this.$store.dispatch('setError', 'Area is required')
        return
      }
      if (!this.form.restaurant_ids.length) {
        this.$store.dispatch('setError', 'Select at least one business')
        return
      }
      if (this.form.waiting_lat === null || this.form.waiting_lng === null) {
        this.$store.dispatch('setError', 'Select waiting location on map')
        return
      }

      try {
        const payload = {
          id: this.group.id,
          name: this.form.name,
          area_id: this.form.area_id,
          restaurant_ids: this.form.restaurant_ids,
          waiting_lat: this.form.waiting_lat,
          waiting_lng: this.form.waiting_lng,
          status: this.form.status
        }

        await this.$store.dispatch('updateRestaurantGroup', payload)
        this.closeDialog()
      } catch (error) {
        console.error(error)
      }
    }
  }
}
</script>

<style scoped>
.delivery-card {
  border-radius: 20px;
  padding: 28px;
}
.header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
}
.title {
  font-size: 22px;
  font-weight: 600;
}
.subtitle {
  font-size: 14px;
  color: #6b7280;
}
.field {
  margin-bottom: 22px;
}
.label {
  font-size: 14px;
  font-weight: 600;
}
.required {
  color: #ef4444;
}
.map-container {
  border-radius: 16px;
  overflow: hidden;
  height: 220px;
  margin-top: 10px;
}
.real-map {
  height: 100%;
}
.footer {
  display: flex;
  justify-content: space-between;
  margin-top: 28px;
}
.cancel-btn {
  width: 48%;
  border-radius: 30px;
}
.save-btn {
  width: 48%;
  border-radius: 30px;
  background-color: #11a8c9 !important;
  color: white !important;
}
.chips {
  margin-top: 10px;
}
.chip {
  margin-right: 8px;
  margin-bottom: 8px;
  background: #eef2f7;
}
</style>
